import React, { useEffect, useState, useContext, useRef } from 'react';
import classes from './AssessmentWizard.module.scss';
import pagesClasses from '@/features/allHome/styles/pages.module.scss';
import PageTitle from '@/features/allHome/containers/Assessment/PageTitle';
import RadioButtonNavList from '@/features/allHome/containers/Assessment/RadioButtonNavList/RadioButtonNavList';
import OptionList from '@/features/allHome/containers/Assessment/OptionList/OptionList';
import LocationPage from '@/features/allHome/containers/Assessment/LocationPage/LocationPage';
import ContentPage from '@/features/allHome/containers/Assessment/ContentPage/ContentPage';
import PreviousButton from '@/components/PreviousButton/PreviousButton';
import NextButton from '@/features/allHome/components/NextButton/NextButton';
import RestartButton from '@/features/allHome/components/RestartButton/RestartButton';
import t from '@/features/allHome/utils/Language/AllHome-translations';
import Router from 'next/router';
import { StateContext } from '@/features/allHome/context/state.context';
import useStateConfig from '@/features/allHome/hooks/useStateConfig';
import useLocation from '@/hooks/useLocation';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Container from 'react-bootstrap/Container';
import Card from 'react-bootstrap/Card';
import ContentBox from '@/components/ContentBox/ContentBox';
import useMixpanel from '@/hooks/useMixpanel';
import { LocationContext } from '@/context/location.context';

/**
 * @returns React Component
 */
const AssessmentWizard = (props) => {
  const config = props.config;
  const [columns, setColumns] = useState([]);
  const [goToState, setGoToState] = useState(false);
  const pageRef = useRef();
  const [pageProps, setPageProps] = useState({});
  const [trackAnswer, setTrackAnswer] = useState({});
  const { hasAnyNextQuestionPage, getNextEnabledState, totalEnabledStates } = useStateConfig();
  const stateContext = useContext(StateContext);
  const { locationContext } = useLocation();
  const [showNavigation, setShowNavigation] = useState(true);
  const [errorMessageOnLocation, setErrorMessageOnLocation] = useState(false);
  // Can initialize on /stayhousedbay area OR on Q1
  const isInitialized = useRef(false);
  const { track } = useMixpanel();
  const { locationHelpers } = useContext(LocationContext);

  const resetAnswers = { tags: [], properties: [], userType: 'self', supportType: 'staying-housed' };

  useEffect(() => {
    if (stateContext.config) {
      setColumns(stateContext.currentState.columns);
      setPageProps(stateContext.currentState);
      if (stateContext.currentState.eventName) {
        setTrackAnswer({
          step: stateContext.currentState.eventName,
          pro: stateContext.answers['userType'] == 'pro'
        });
      }
    }
    if (
      !isInitialized.current &&
      (stateContext.currentStateIndex == 0 || !stateContext.currentStateIndex) &&
      locationContext.location !== undefined
    ) {
      isInitialized.current = true;
      initState();
    }
    scrollToTop();
  }, [stateContext.config, stateContext.currentState, locationContext.location]);

  useEffect(() => {
    // TODO: If not needed anymore then it will be removed after UAT
    // renderDebuggerInfo();
    // console.log(stateContext.config);
  }, [stateContext.answers]);

  useEffect(() => {
    if (goToState) {
      getNextEnabledState({});
      setGoToState(false);
    }
  }, [goToState]);

  const saveLocationFn = (place) => {
    let location = {};
    if (place) {
      locationContext.updateLocationByPlace(place);
      location = locationHelpers.buildLocationFromPlace(place);
      if (!location?.county) {
        setErrorMessageOnLocation(true);
      } else {
        setErrorMessageOnLocation(false);
      }
    }
  };

  const getPageIndex = () => {
    const pageIndex = config?.pages.findIndex((page) => '#' + page?.enableState?.answerValueMatches === props?.page);
    return pageIndex;
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  const initState = () => {
    let pageIndex = getPageIndex();
    if (pageIndex > -1) {
      setShowNavigation(false);
    } else {
      // If location is set on /stayhousedbay area, skip Q1
      pageIndex = locationContext.getLocation()?.county ? 1 : 0;
    }
    stateContext.setConfig(config);
    stateContext.setCurrentState(config.pages[pageIndex]);
    stateContext.setCurrentStateIndex(pageIndex);
    stateContext.setAnswers((answers) => ({
      ...answers,
      ...resetAnswers
    }));
  };

  const updateAnswers = (answerType, value, goNextOnSelection) => {
    let newAnswer = {};
    let willTrackAnswer = false;
    switch (answerType) {
      case 'tags':
        if (value) {
          newAnswer = { tags: value };
          willTrackAnswer = value;
        }
        break;
      case 'properties':
        if (value.some((v) => stateContext.answers['properties'].includes(v))) {
          newAnswer = { properties: stateContext.answers['properties'].filter((prop) => !value.includes(prop)) };
        } else if (value.length) {
          willTrackAnswer = value;
          newAnswer = { properties: stateContext.answers['properties'].concat(value) };
        } else {
          newAnswer = { properties: [] };
        }
        break;
      case 'location':
        if (value) {
          newAnswer = { location: value };
          willTrackAnswer = locationContext.location?.locationName;
        } else {
          newAnswer = { location: {} };
        }
        break;
      case 'optionLink':
        newAnswer = { optionLink: value || '' };
        break;
      default:
        newAnswer = {};
        newAnswer[answerType] = value;
        willTrackAnswer = value;
        break;
    }
    stateContext.setAnswers((answers) => ({
      ...answers,
      ...newAnswer
    }));
    if (willTrackAnswer) {
      track('Answer in Stay_Housed_Bay_Area', {
        questionType: answerType,
        answer: willTrackAnswer,
        pro: stateContext.answers['userType'] == 'pro'
      });
    }
    if (goNextOnSelection) {
      setGoToState(true);
    }
  };

  const renderDebuggerInfo = () => {
    return (
      <Container>
        <Card>
          <Card.Body>
            <p>Debugging info:</p>
            <p>location: {locationContext.location?.lat}</p>
            <p>userType: {stateContext.answers?.userType}</p>
            <p>supportType: {stateContext.answers?.supportType}</p>
            <p>selectedTags: {stateContext.answers?.tags?.join(', ')}</p>
            <p>selectedProperties: {stateContext.answers?.properties?.join(', ')}</p>
            <p>selectionEvictionOptions: {stateContext.answers?.evictionOptions?.join(', ')}</p>
          </Card.Body>
        </Card>
      </Container>
    );
  };

  const actions = (page) => {
    return {
      back: () => {
        getNextEnabledState({ doReverse: true });
      },
      submitRoute: () => {
        if (pageProps.type == 'question') {
          track('Step in Stay_Housed_Bay_Area', trackAnswer);
        }
        updateAnswers('location', { lat: locationContext.getLocation()?.lat, long: locationContext.getLocation()?.lng });
        Router.push(`/stayhousedbayarea/results`);
      },
      next: () => {
        if (pageProps.type == 'question') {
          track('Step in Stay_Housed_Bay_Area', trackAnswer);
        }
        getNextEnabledState({ doReverse: false });
      },
      restart: () => {
        locationContext.clearLocation();
        stateContext.setCurrentState(config.pages[0]);
        stateContext.setCurrentStateIndex(0);
        stateContext.setAnswers((answers) => ({ ...answers, ...resetAnswers }));
      },
      skip: () => {},
      home: () => {
        Router.push(`/stayhousedbayarea`);
      }
    };
  };

  const renderElement = (element, goNextOnSelection) => {
    switch (element.controlType) {
      case 'pagetitle':
        return (
          <PageTitle additionalClasses={classes.pageTitle} updateAnswers={updateAnswers} element={element} t={t} answers={stateContext.answers} />
        );
      case 'radiobuttonnavlist':
        return (
          <RadioButtonNavList
            additionalClasses={`${classes.radioButtonNavList}`}
            element={element}
            answers={stateContext.answers}
            t={t}
            onSelect={updateAnswers}
          />
        );
      case 'optionList':
        return (
          <OptionList
            additionalClasses={`${classes.radioButtonNavList}`}
            element={element}
            answers={stateContext.answers}
            t={t}
            onSelect={updateAnswers}
          />
        );
      case 'optionLinkList':
        return (
          <>
            {element?.header && <div className={classes.questionNumberLabel}>{t(element?.header)}</div>}
            <RadioButtonNavList
              additionalClasses={`${classes.radioButtonNavList}`}
              element={element}
              answers={stateContext.answers}
              t={t}
              onSelect={updateAnswers}
              type={'link'}
            />
          </>
        );
      case 'contentPage':
        return <ContentPage element={element.content} t={t} />;
      case 'linkList':
        return 'TODO';
      case 'location':
        return (
          <LocationPage
            element={element}
            locationObject={locationContext?.location || {}}
            saveLocationFn={saveLocationFn}
            answers={stateContext.answers}
            t={t}
            showErrorMessage={errorMessageOnLocation}
          />
        );
      default:
        console.warn('Unsupported element provided to Resource Search Wizard', element.controlType);
        return <React.Fragment />;
    }
  };

  const renderPrevious = () => {
    return (
      <PreviousButton
        action={stateContext.currentStateIndex === 0 ? actions().home : actions().back}
        text={t(`${stateContext.currentStateIndex === 0 ? 'Home' : 'Previous Question'}`)}
      />
    );
  };

  const disableNextButton = () => {
    let disableButton = false;
    if (
      (stateContext?.currentState?.name === 'chooselocation' && !locationContext.getLocation()?.county) ||
      (stateContext?.currentState?.name === 'choosetags' && stateContext?.answers?.tags?.length === 0) ||
      (stateContext?.currentState?.name === 'evictiontype' &&
        (!stateContext?.answers?.evictionOptions || stateContext?.answers?.evictionOptions?.length === 0))
    ) {
      disableButton = true;
    }
    return disableButton;
  };

  const renderNext = (pageType) => {
    if (pageType == 'ending') {
      return (
        <RestartButton
          additionalClasses={classes.nextButton}
          icon={{ style: 'fas', font: 'refresh', position: 'left' }}
          action={actions().restart}
          text={t('Restart')}
        />
      );
    } else {
      if (hasAnyNextQuestionPage()) {
        return (
          <NextButton
            additionalClasses={classes.nextButton}
            action={actions().next}
            text={t('Next')}
            icon={{ style: 'fas', font: 'caret-right', position: 'right' }}
            disabled={disableNextButton()}
          />
        );
      } else {
        return (
          <NextButton
            additionalClasses={classes.nextButton}
            icon={{ style: 'fas', font: 'circle-check', position: 'left' }}
            action={actions().submitRoute}
            text={t('See results')}
            disabled={!locationContext.getLocation()?.county}
          />
        );
      }
    }
  };

  const renderQuestion = () => {
    return (
      <>
        {columns.map((column, index) => {
          return (
            <Col xs="12" lg="6" className={classes.containerColumn} key={index}>
              {/* TODO - update this so it shows an accurate count of how many pages (enabled states) remain */}
              {stateContext.config && index === 0 && stateContext.currentState.type !== 'ending' && (
                <div className={classes.questionNumberLabel} data-cy="question-total">
                  {t('Question')} {stateContext.currentState.page} {t('of')} {totalEnabledStates(stateContext.config.pages) + 1}
                </div>
              )}
              {column['elements'].map((element, index) => {
                return (
                  <div key={index} className={`el-${element.controlType}`}>
                    {renderElement(element, pageProps.goNextOnSelection)}
                  </div>
                );
              })}
            </Col>
          );
        })}
      </>
    );
  };

  return (
    <>
      {stateContext.config && (
        <section ref={pageRef} id="AssessmentWizard" className={`${classes.AssessmentWizard} page-${pageProps.name}`}>
          <Container fluid className={classes.limitMaxWidth}>
            {showNavigation && <Row className={classes.previousButtonContainer}>{renderPrevious()}</Row>}
            <Row className={classes.assessmentContainer}>
              <ContentBox additionalClasses={pagesClasses.shbaContentBox} content={renderQuestion()} />
            </Row>
            {showNavigation && <Row className={classes.nextButtonContainer}>{renderNext(pageProps.type)}</Row>}
          </Container>
          {/* TODO: If not needed anymore then it will be removed after UAT */}
          {/* {renderDebuggerInfo()} */}
        </section>
      )}
    </>
  );
};

export default AssessmentWizard;
